/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import DatePicker from "react-datepicker";
import { useToasts } from "react-toast-notifications";
import { PatientsContext } from ".";
import { useParams } from "react-router-dom";
import fetchHelper from "../../services/fetchHelper";
import CycleVisualizer from "../../components/CycleVisualizer";
import _ from "lodash";
import { serverTimeZoneName, serverTimeZoneOffset } from "../../../package.json";
import "./remoteMonitoring.scss";
import savingSpinner from "../../assets/esign-ui-icons/saving-ico-white.svg";
import { CycleFlag } from "../../constants/DevicesConstants";

const RemoteMonitoring = () => {
  const { i } = useParams();
  const { addToast } = useToasts();
  const { rMonitoring, setRMonitoring } = useContext(PatientsContext);
  const [data, setData] = useState({});
  const [rMonitoringCycles30, setRMonitoringCycles30] = useState(null);
  const [rMonitoringCycles90, setRMonitoringCycles90] = useState(null);
  const [interrogations, setInterrogations] = useState([]);
  const [devices, setDevices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [firstInt, setFirstInt] = useState(true);
  const [filteredInt, setFilteredInt] = useState([]);
  const requiresRelation = useRef(null);
  const deviceInfo = useRef(null);
  const [oldCycleState, setOldCycleState] = useState(null);

  useEffect(() => {
    fetchInterrogations();
    fetchDevices();
    fetchData();
  }, []);

  useEffect(() => {
    if (rMonitoring?.data && devices.length > 0) {
      requiresRelation.current = validateDeviceRelation();
      setMonitoringData(requiresRelation.current ? null : devices[0].id);
    }
  }, [(rMonitoring || {}).data, devices]);

  const cycleStateChanged = useMemo(() => {
    const hasCycleFlagChanged = (oldCycleState?.nintyDayFlag !== data?.nintyDayFlag) ||
      (oldCycleState?.thirtyDayFlag !== data?.thirtyDayFlag);

    const hasCycleDateChanged = (data?.nintyDaySDate && !_.isEqual(oldCycleState?.nintyDaySDate,  data?.nintyDaySDate)) ||
      (data?.thirtyDaySDate && !_.isEqual(oldCycleState?.thirtyDaySDate,data?.thirtyDaySDate))

    if (hasCycleFlagChanged) {
      const isCycleActiveAndNotSet = (data.nintyDayFlag === CycleFlag.ACTIVE && !data.nintyDaySDate)
        || (data.thirtyDayFlag === CycleFlag.ACTIVE && !data.thirtyDaySDate);

      const isCycleInactive = !oldCycleState && (data.nintyDayFlag === CycleFlag.INACTIVE && data.thirtyDayFlag === CycleFlag.INACTIVE)

      return isCycleActiveAndNotSet || isCycleInactive
    }

    return !hasCycleDateChanged;
  }, [data, oldCycleState]);

  const parseDateFromServerFormat = (currentDate) => {
    let tmpDate = new Date(currentDate);
    const day = parseInt(
      tmpDate.toLocaleString(undefined, { timeZone: serverTimeZoneName, day: "numeric", hour12: false })
    );
    const month = parseInt(
      tmpDate.toLocaleString(undefined, { timeZone: serverTimeZoneName, month: "numeric", hour12: false })
    );
    const year = parseInt(
      tmpDate.toLocaleString(undefined, { timeZone: serverTimeZoneName, year: "numeric", hour12: false })
    );
    return new Date(year, month - 1, day, 0, 0, 0);
  };

  const parseDateToServerFormat = (currentDate) => {
    let offset = parseInt(serverTimeZoneOffset);
    let year = currentDate.getFullYear();
    let month = currentDate.getMonth();
    let date = currentDate.getDate();
    let newDate = new Date(Date.UTC(year, month, date, 0, 0, 0));
    newDate.setHours(newDate.getHours() - offset); // add the difference between server time zone and UTC
    let hourAtPT = parseInt(
      newDate.toLocaleString(undefined, { timeZone: serverTimeZoneName, hour: "numeric", hour12: false })
    );
    if (hourAtPT === 1) {
      // if the hour is 1 AM, them this means daylight saving time
      newDate.setHours(newDate.getHours() + (offset > 0 ? 1 : -1)); // make adjust to daylight saving time
    }
    return newDate;
  };

  const fetchInterrogations = async () => {
    try {
      const urlParams = {
        where: JSON.stringify({ "patient.id": { $eq: i } }),
        match: "AND",
        matchInner: "OR",
        sort: "-interrogationDate",
      };

      const urlSearch = new URLSearchParams(urlParams);

      const data = await fetchHelper.get({
        url: "/patientmanagement/rest/interrogations/getNewInbox?" + urlSearch,
      });
      setInterrogations(data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (firstInt) {
      if (devices.length > 0 && interrogations.length > 0) {
        filterDeviceInterrogations(devices[0]?.id);
        setFirstInt(false);
      }
    }
  }, [devices, interrogations, firstInt]);

  const fetchDevices = async () => {
    try {
      const data = await fetchHelper.get({ url: `/patientmanagement/rest/devices/findByPatientId/${i}` });
      setDevices(data);
    } catch ({ errors }) {
      errors &&
        errors.forEach((err) => {
          addToast(err.msg, {
            appearance: "error",
            autoDismiss: false,
          });
        });
    }
  };

  const handleRemoteMonitoring = (e) => {
    const deviceId = parseInt(e.target.value);
    const filteredDevice = _.filter(rMonitoring.data, (device) => {
      return device.device?.id === deviceId;
    })[0];
    let dateServerFormat;
    if (filteredDevice?.nintyDayFlag == "ACTIVE") {
      dateServerFormat = parseDateToServerFormat(filteredDevice?.nintyDaySDate);
      fetchCycles(dateServerFormat.getTime(), 90);
    }
    if (filteredDevice?.thirtyDayFlag == "ACTIVE") {
      dateServerFormat = parseDateToServerFormat(filteredDevice?.thirtyDaySDate);
      fetchCycles(dateServerFormat.getTime(), 30);
    }
    setMonitoringData(deviceId);
    filterDeviceInterrogations(deviceId);
  };

  const fetchData = async () => {
    try {
      const data = await fetchHelper.get({
        url: "/patientmanagement/rest/rmonitoring/findByPatientId/" + i,
      });
      data &&
        data.forEach((item, key) => {
          if (item.thirtyDayFlag === "ACTIVE") {
            if (key == 0) fetchCycles(item?.thirtyDaySDate, 30);
            item.thirtyDaySDate = parseDateFromServerFormat(item.thirtyDaySDate);
          }
          if (item.nintyDayFlag === "ACTIVE") {
            if (key == 0) fetchCycles(item?.nintyDaySDate, 90);
            item.nintyDaySDate = parseDateFromServerFormat(item.nintyDaySDate);
          }
        });
      setRMonitoring({ data });
      setOldCycleState(data[0]);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCycles = async (startCycleDate, cycleType) => {
    try {
      const data = await fetchHelper.get({
        url:
          "/patientmanagement/rest/rmonitoring/cycles?startCycleDate=" +
          startCycleDate +
          "&cycleType=" +
          cycleType +
          "",
      });
      if (cycleType == 30) {
        setRMonitoringCycles30(data);
      }
      if (cycleType == 90) {
        setRMonitoringCycles90(data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      let d;
      let remoteMonitoringData = { ...data };
      let updatedMonitoring = rMonitoring.data;
      const monitoringIndex = rMonitoring.data.findIndex(
        (monitoring) => monitoring?.device?.id === data.device.id
      );
      if (remoteMonitoringData.thirtyDayFlag === "ACTIVE") {
        remoteMonitoringData.thirtyDaySDate = parseDateToServerFormat(remoteMonitoringData.thirtyDaySDate);
        fetchCycles(remoteMonitoringData.thirtyDaySDate.getTime(), 30);
      }
      if (remoteMonitoringData.nintyDayFlag === "ACTIVE") {
        remoteMonitoringData.nintyDaySDate = parseDateToServerFormat(remoteMonitoringData.nintyDaySDate);
        fetchCycles(remoteMonitoringData.nintyDaySDate.getTime(), 90);
      }
      if (remoteMonitoringData.id) {
        d = await fetchHelper.put(
          "/patientmanagement/rest/rmonitoring/" + remoteMonitoringData.id,
          remoteMonitoringData
        );
      } else {
        const defaultValues = {
          patient: { id: i },
          nintyDayFlag: "INACTIVE",
          nintyDaySDate: null,
          ninetyDaysSchedule: { id: null },
          thirtyDayFlag: "INACTIVE",
          thirtyDaySDate: null,
          thirtyDaysSchedule: { id: null },
          nintyDayVn: "",
          thirtyDayVn: "",
          nintyDayUpdatedOn: null,
          thirtyDayUpdatedOn: null,
        };
        d = await fetchHelper.post("/patientmanagement/rest/rmonitoring/", {
          ...defaultValues,
          ...remoteMonitoringData,
        });
      }
      if (d.thirtyDayFlag === "ACTIVE") {
        d.thirtyDaySDate = parseDateFromServerFormat(d.thirtyDaySDate);
      }
      if (d.nintyDayFlag === "ACTIVE") {
        d.nintyDaySDate = parseDateFromServerFormat(d.nintyDaySDate);
      }
      if (monitoringIndex >= 0) {
        updatedMonitoring[monitoringIndex] = d;
      } else if (requiresRelation.current) {
        updatedMonitoring = [d];
      } else {
        updatedMonitoring.push(d);
      }
      setData(d);
      setOldCycleState(d);
      setRMonitoring({ data: updatedMonitoring });
    } catch ({ errors }) {
      setSaving(false);
      errors &&
        errors.forEach((err) => {
          addToast(err.msg, {
            appearance: "error",
            autoDismiss: false,
          });
        });
    }
    setSaving(false);
  };

  const setMonitoringData = (deviceID) => {
    let ret = rMonitoring.data[0];
    deviceInfo.current = deviceID ? devices.find((device) => device.id === deviceID) : null;
    if (requiresRelation.current && deviceID) {
      ret = { ...rMonitoring.data[0], device: { id: deviceID } };
    } else if (deviceID) {
      const relationData = rMonitoring.data.find((monitoring) => monitoring?.device?.id === deviceID);
      const monitoringData = relationData || {};
      ret = { ...monitoringData, device: { id: deviceID } };
    }
    setData(ret);
    setOldCycleState(ret);
  };

  const validateDeviceRelation = () => {
    return rMonitoring.data.length > 0 && rMonitoring.data.every((monitoring) => !monitoring.device);
  };

  const filterDeviceInterrogations = (deviceID) => {
    if (interrogations.length > 0) {
      const filteredValue = interrogations.filter((item) => item?.device?.id == deviceID);
      setFilteredInt(filteredValue);
    }
  };

  const onChangeCycleFlag = (e, type) => {
    const { checked } = e.target;
    setData((data) => ({
      ...data,
      [`${type}Flag`]: checked ? "ACTIVE" : "INACTIVE",
      [`${type}SDate`]: null,
      [`${type}Vn`]: "",
      [`${type}UpdatedOn`]: new Date().toDateInputValue(),
    }));
  };

  const onChangeVisitNumber = (e, item) => {
    const { value } = e.target;
    const visitNumber = value == "" ? undefined : value;
    setData({ ...data, [item]: visitNumber });
  };

  const handle30DatePicker = (date) => {
    setData((data) => ({ ...data, thirtyDaySDate: date }));
    const serverDate = parseDateToServerFormat(date);
    const cyclesDate = serverDate.getTime();
    fetchCycles(cyclesDate, 30);
  };

  const handle90DatePicker = (date) => {
    setData((data) => ({ ...data, nintyDaySDate: date }));
    const serverDate = parseDateToServerFormat(date);
    const cyclesDate = serverDate.getTime();
    fetchCycles(cyclesDate, 90);
  };

  const shouldDisableExplantedCycle = (deviceStatus, monitoringStatus = "INACTIVE") => {
    const isExplantedDevice = deviceStatus === "EXPLANTED";
    const isInactiveMonitoring = monitoringStatus === "INACTIVE";

    return isExplantedDevice && isInactiveMonitoring;
  };

  const shouldDisableExplantedSaving = (device) => {
    const isExplantedDevice = device?.status === "EXPLANTED";
    const monitoringData = rMonitoring.data.find((item) => item.device?.id === device?.id);
    const thirtyCycle = monitoringData?.thirtyDayFlag === "ACTIVE";
    const nintyCycle = monitoringData?.nintyDayFlag === "ACTIVE";

    if (thirtyCycle || nintyCycle || !isExplantedDevice) {
      return false;
    } else {
      return true;
    }
  };

  return (
    !loading && (
      <div className="tabContent remote-monitoring">
        <div className="content">
          <div className="grpHdrPg">
            <h3>
              Patient Profile: <span>John Smith</span>
            </h3>
          </div>
          <div className="grpHdr">
            <h3>Remote Monitoring Billing Schedule</h3>
          </div>
          {devices.length === 0 ? (
            <div className="alert alert-danger">
              <strong>No devices available.</strong> To enable the remote monitoring feature please add
              devices to the patient.
            </div>
          ) : (
            <div>
              {!data.device && (
                <div className="alert alert-advisory">
                  <strong>No selected device.</strong> Please define a device for the existing monitoring
                  cycle and then save.
                </div>
              )}
              {deviceInfo.current?.status === "EXPLANTED" && (
                <div className="alert alert-advisory">
                  Remote monitoring schedule dates cannot be changed on explanted devices.
                </div>
              )}
              <div className="row rowStretch">
                <div className="col no-flex-grow" style={{ width: "22%" }}>
                  <label>
                    <span>Device Type</span>
                    <select onChange={handleRemoteMonitoring}>
                      {!data.device && <option value={null}>-</option>}
                      {devices.map((device, index) => (
                        <option key={index} value={device.id}>
                          {`${device.deviceType} - ${device.serialNumber}`}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col-m-100 cycle-type">
                  <label className="formLi cycle-type-formLi">
                    <input
                      type="checkbox"
                      name="thirtyDayFlag"
                      className="chk"
                      checked={data.thirtyDayFlag === "ACTIVE"}
                      onChange={(e) => onChangeCycleFlag(e, "thirtyDay")}
                      disabled={shouldDisableExplantedCycle(deviceInfo.current?.status, data.thirtyDayFlag)}
                    />
                    <span className="chkmrk" />
                    <span className="chk-label">Cycle Type (30)</span>
                  </label>
                </div>
                <div className="col col-m-50 col20">
                  <label className="inCal">
                    <span>Manual Schedules</span>
                  </label>
                  <DatePicker
                    selected={data.thirtyDaySDate || ""}
                    onChange={(date) => handle30DatePicker(date)}
                    disabled={data.thirtyDayFlag !== "ACTIVE" || deviceInfo.current?.status === "EXPLANTED"}
                  />
                </div>
                <div className="col col-m-50 col20">
                  <label>
                    <span>Visit</span>
                    <input
                      data-testid="thirty-day-visit"
                      className="not-clickable"
                      type="text"
                      value={data.thirtyDayVn || ""}
                      disabled={true}
                      onChange={(e) => onChangeVisitNumber(e, "thirtyDayVn")}
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch">
                <div className="col col-m-100 cycle-type">
                  <label className="formLi cycle-type-formLi cycle-type-top">
                    <input
                      type="checkbox"
                      name="nintyDayFlag"
                      className="chk"
                      checked={data.nintyDayFlag === "ACTIVE"}
                      onChange={(e) => onChangeCycleFlag(e, "nintyDay")}
                      disabled={shouldDisableExplantedCycle(deviceInfo.current?.status, data.nintyDayFlag)}
                    />
                    <span className="chkmrk" />
                    <span className="chk-label">Cycle Type (90)</span>
                  </label>
                </div>
                <div className="col col-m-50 col20">
                  <label className="inCal">
                    <span>Manual Schedules</span>
                    <i className="ti ti-calendar" />
                  </label>
                  <DatePicker
                    selected={data.nintyDaySDate || ""}
                    onChange={(date) => handle90DatePicker(date)}
                    disabled={data.nintyDayFlag !== "ACTIVE" || deviceInfo.current?.status === "EXPLANTED"}
                  />
                </div>
                <div className="col col-m-50 col20">
                  <label>
                    <span>Visit</span>
                    <input
                      data-testid="ninty-day-visit"
                      className="not-clickable"
                      type="text"
                      value={data.nintyDayVn || ""}
                      disabled={true}
                      onChange={(e) => onChangeVisitNumber(e, "nintyDayVn")}
                    />
                  </label>
                </div>
              </div>
              <div className="row rowStretch cycle-row">
                <div className="col col-s-100 col100">
                  <div className="scheduler">
                    <CycleVisualizer
                      startDate={(data.thirtyDaySDate && new Date(data.thirtyDaySDate)) || ""}
                      interrogations={filteredInt}
                      rMonitoringCycles={rMonitoringCycles30}
                    />
                    <CycleVisualizer
                      startDate={(data.nintyDaySDate && new Date(data.nintyDaySDate)) || ""}
                      interrogations={filteredInt}
                      cycle={90}
                      rMonitoringCycles={rMonitoringCycles90}
                    />
                  </div>
                </div>
              </div>
              <button
                className="btn btnTeal"
                style={{ marginBottom: 10 }}
                disabled={saving || !data.device || shouldDisableExplantedSaving(deviceInfo.current) || cycleStateChanged}
                onClick={(e) => {
                  e.preventDefault();
                  save();
                }}
              >
                <img id="loading-spinner-1" src={savingSpinner} style={{ width: 0, height: 0 }} />
                {saving ? (
                  <img id="loading-spinner-2" src={savingSpinner} style={{ width: 15, height: 15 }} />
                ) : (
                  "Save"
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    )
  );
};

export default RemoteMonitoring;
